import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Modal } from '../reusable';
import { getActiveRequestRejectionReasons } from './planningDomain';
import { useCommercialCatalogRefresh } from '../../hooks/useCommercialCatalogRefresh';

/** Ventana para cancelar una solicitud de producto: motivo obligatorio y notas opcionales. */
const ProductRequestRejectModal = ({ isOpen, onClose, onConfirm, request, isDarkMode = false }) => {
  const catalogTick = useCommercialCatalogRefresh();
  const rejectionReasons = useMemo(() => getActiveRequestRejectionReasons(), [catalogTick]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setNotes('');
    setError('');
    setIsSaving(false);
  }, [isOpen, request?.request_id]);

  if (!request) return null;

  const text = isDarkMode ? '#f4f4f5' : '#111827';
  const muted = isDarkMode ? '#a1a1aa' : '#6b7280';
  const border = isDarkMode ? '#3f3f46' : '#d1d5db';
  const fieldBg = isDarkMode ? '#18181b' : '#ffffff';
  const label = { display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 6 };
  const field = {
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${border}`,
    borderRadius: 8,
    background: fieldBg,
    color: text,
    fontSize: 13,
    padding: '8px 10px',
    fontFamily: 'inherit',
  };
  const footerBtn = (bg, enabled = true) => ({
    height: 34,
    padding: '0 12px',
    borderRadius: 8,
    border: bg ? 'none' : `1px solid ${border}`,
    background: bg || fieldBg,
    color: bg ? '#ffffff' : text,
    fontSize: 12,
    fontWeight: 600,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.5,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  });

  const confirm = async () => {
    if (!reason) {
      setError('Selecciona el motivo de la cancelación.');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      await onConfirm?.({ reason, notes: notes.trim() });
    } catch (err) {
      setError(err?.message || 'No se pudo cancelar la solicitud.');
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSaving) onClose?.();
      }}
      isDarkMode={isDarkMode}
      size="md"
      title={`Cancelar solicitud ${request.code || ''}`.trim()}
      subtitle={[request.sale_code, request.organization_name].filter(Boolean).join(' · ') || undefined}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
          <button type="button" onClick={onClose} disabled={isSaving} style={footerBtn()}>
            Volver
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={isSaving || !reason}
            style={footerBtn('#dc2626', !isSaving && Boolean(reason))}
          >
            <X size={14} /> {isSaving ? 'Cancelando…' : 'Cancelar solicitud'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ fontSize: 12, color: muted, lineHeight: 1.45 }}>
          La solicitud quedará <strong style={{ color: text }}>Cancelada</strong> y la entrega asociada pasará a{' '}
          <strong style={{ color: text }}>En Reprogramación</strong>. Para volver a pedir esos productos hay que
          generar una nueva solicitud.
        </div>
        <div>
          <label htmlFor="reject-reason" style={label}>
            Motivo de la cancelación *
          </label>
          <select
            id="reject-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            style={{ ...field, height: 36 }}
          >
            <option value="">Selecciona un motivo…</option>
            {rejectionReasons.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="reject-notes" style={label}>
            Notas (opcional)
          </label>
          <textarea
            id="reject-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={4}
            placeholder="Detalle para Comercial: qué falta, alternativas, etc."
            style={{ ...field, resize: 'vertical' }}
          />
          {request.notes ? (
            <div style={{ fontSize: 11, color: muted, marginTop: 6 }}>
              Se agregarán a las notas existentes de la solicitud.
            </div>
          ) : null}
        </div>
        {error ? <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{error}</div> : null}
      </div>
    </Modal>
  );
};

export default ProductRequestRejectModal;
